'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './Footer.module.scss'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'

// Vimeo Player API types
interface VimeoPlayer {
  on: (event: string, callback: () => void) => void
  off: (event: string, callback: () => void) => void
  destroy: () => void
  ready: () => Promise<void>
  play: () => Promise<void>
}

interface FooterProps {
  titleLevel?: 'h1' | 'h2'
}

export const Footer: React.FC<FooterProps> = ({ titleLevel = 'h2' }) => {

  const videoRef = useRef<HTMLDivElement>(null)
  const vimeoPlayerRef = useRef<VimeoPlayer | null>(null)
  const vimeoContainerRef = useRef<HTMLDivElement>(null)
  const throttleRef = useRef<NodeJS.Timeout | null>(null)
  
  // Animation states
  const [titleInView, setTitleInView] = useState(false)
  const [gridInView, setGridInView] = useState(false)
  const [videoInView, setVideoInView] = useState(false)
  const [baseInView, setBaseInView] = useState(false)
  
  // Video loading states
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isVimeoAPILoaded, setIsVimeoAPILoaded] = useState(false)
  
  // Refs for intersection observers
  const titleRef = useRef<HTMLHeadingElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const baseRef = useRef<HTMLDivElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  

  
  // Always allow video loading and performance optimizations
  const allowVideoLoading = true

  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad
  
  // Check if video should be visible - video must be loaded, playing, and in view
  const shouldShowVideo = allowVideoLoading && (isAdminMode || (isVideoLoaded && isVideoPlaying && videoInView))
  
  // Check if we should hide the black cover - only when video is actually playing
  const shouldHideBlackCover = allowVideoLoading && (isAdminMode || (isVideoLoaded && isVideoPlaying && videoInView))


  // Load Vimeo Player API - always load for video functionality
  const loadVimeoAPI = useCallback(() => {
    if (!allowVideoLoading) return Promise.resolve()
    
    if ((window as unknown as { Vimeo?: unknown }).Vimeo || isVimeoAPILoaded) {
      setIsVimeoAPILoaded(true)
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://player.vimeo.com/api/player.js'
      script.onload = () => {
        setIsVimeoAPILoaded(true)
        resolve()
      }
      document.head.appendChild(script)
    })
  }, [isVimeoAPILoaded, allowVideoLoading])

  // Initialize Vimeo Player - allow for everyone
  const initializeVimeoPlayer = useCallback(async () => {
    if (!allowVideoLoading || !vimeoContainerRef.current || !isVimeoAPILoaded || vimeoPlayerRef.current) {
      return
    }

    try {
      await loadVimeoAPI()
      
      // Hardcoded unlisted video ID with hash parameter for footer
      const vimeoId = '1108928059'
      const hashParam = '940b09c208'
      
      // Build player options - use URL for unlisted videos with hash, separate options for background settings
      const playerOptions: Record<string, unknown> = {
        url: `https://player.vimeo.com/video/${vimeoId}?h=${hashParam}&dnt=1`,
        responsive: false,
        width: 1920,
        height: 1080,
        background: true,
        autoplay: true,
        loop: true,
        muted: true,
        controls: false,
        byline: false,
        title: false,
        portrait: false,
        dnt: true
      }
      
      const player = new (window as unknown as { Vimeo: { Player: new (el: HTMLElement, opts: unknown) => VimeoPlayer } }).Vimeo.Player(vimeoContainerRef.current, playerOptions) as VimeoPlayer

      vimeoPlayerRef.current = player

      // Listen for video events
      player.on('loaded', () => {
        setIsVideoLoaded(true)
      })

      player.on('play', () => {
        setIsVideoPlaying(true)
      })

      // Wait for player to be ready
      await player.ready()
      
      // For mobile Safari, add a small delay before attempting to play
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent)
      const playDelay = isMobileSafari ? 500 : 0
      
      setTimeout(async () => {
        // Manually trigger play for unlisted videos (autoplay might be blocked)
        try {
          await player.play()
        } catch {
          // On Safari mobile, autoplay might fail - set up user interaction fallback
          if (isMobileSafari) {
            const playOnInteraction = async () => {
              try {
                await player.play()
                document.removeEventListener('touchstart', playOnInteraction)
                document.removeEventListener('click', playOnInteraction)
              } catch {
                // Ignore play errors
              }
            }
            
            document.addEventListener('touchstart', playOnInteraction, { once: true })
            document.addEventListener('click', playOnInteraction, { once: true })
          }
          
          // Still call setIsVideoPlaying to show the video even if autoplay fails
          setIsVideoPlaying(true)
        }
      }, playDelay)
      
    } catch {
      // Fallback: show video after a delay
      setTimeout(() => {
        setIsVideoLoaded(true)
        setIsVideoPlaying(true)
      }, 2000)
    }
  }, [isVimeoAPILoaded, loadVimeoAPI, allowVideoLoading])

  const calculateAndApplyScale = useCallback(() => {
    if (!videoRef.current) return

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const videoWidth = 1920
    const videoHeight = 1080
    
    const maxWidthPercent = 0.9
    const maxHeightPercent = 0.8
    
    const scaleByWidth = (viewportWidth * maxWidthPercent) / videoWidth
    const scaleByHeight = (viewportHeight * maxHeightPercent) / videoHeight
    
    let newScale = Math.min(scaleByWidth, scaleByHeight)
    
    const minScale = 0.19
    const maxScale = 0.9
    newScale = Math.max(minScale, Math.min(maxScale, newScale))
    
    videoRef.current.style.transform = `scale(${newScale})`
  }, [])

  const throttledResize = useCallback(() => {
    if (throttleRef.current) {
      clearTimeout(throttleRef.current)
    }
    
    throttleRef.current = setTimeout(calculateAndApplyScale, 16)
  }, [calculateAndApplyScale])

  // Create intersection observer for each element
  const createObserver = useCallback((element: HTMLElement, setState: (value: boolean) => void, forVideo = false) => {
    if (!element || isAdminMode) {
      if (isAdminMode) setState(true)
      return null
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        // For video, always trigger when in view (independent of canAnimate)
        // For other elements, respect canAnimate for animations
        if (entry.isIntersecting && (forVideo || canAnimate)) {
          setState(true)
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(element)
    return observer
  }, [canAnimate, isAdminMode])

  // Set up intersection observers
  useEffect(() => {
    const titleObserver = titleRef.current ? createObserver(titleRef.current, setTitleInView) : null
    const gridObserver = gridRef.current ? createObserver(gridRef.current, setGridInView) : null
    const videoObserver = videoContainerRef.current ? createObserver(videoContainerRef.current, setVideoInView, true) : null
    const baseObserver = baseRef.current ? createObserver(baseRef.current, setBaseInView) : null

    return () => {
      titleObserver?.disconnect()
      gridObserver?.disconnect()
      videoObserver?.disconnect()
      baseObserver?.disconnect()
    }
  }, [createObserver])

  useEffect(() => {
    calculateAndApplyScale()

    window.addEventListener('resize', throttledResize)

    return () => {
      window.removeEventListener('resize', throttledResize)
      if (throttleRef.current) {
        clearTimeout(throttleRef.current)
      }
    }
  }, [calculateAndApplyScale, throttledResize])

  // Load Vimeo API when component mounts - always load for video
  useEffect(() => {
    if (allowVideoLoading) {
      loadVimeoAPI()
    }
  }, [loadVimeoAPI, allowVideoLoading])

  // Initialize Vimeo player when API is loaded and video is in view - allow for everyone
  useEffect(() => {
    if (allowVideoLoading && isVimeoAPILoaded && (videoInView || isAdminMode)) {
      initializeVimeoPlayer()
    }
  }, [isVimeoAPILoaded, videoInView, initializeVimeoPlayer, isAdminMode, allowVideoLoading])





  // Cleanup Vimeo player on unmount
  useEffect(() => {
    return () => {
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.destroy()
        vimeoPlayerRef.current = null
      }
    }
  }, [])

  return (
    <footer className={styles.footer}>

      <div className={styles.footer__content}>
                <motion.div 
          ref={titleRef}
          className={styles.footer__title}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: titleInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {titleLevel === 'h1' ? (
            <h1>Get in touch</h1>
          ) : (
            <h2>Get in touch</h2>
          )}
        </motion.div>
        
                <motion.div 
          ref={gridRef}
          className={styles.footer__grid}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: gridInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            delay: 0.2, // Appears after title
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <div className={styles['footer__grid-column']}>
            <a href="mailto:hello@gatherproductions.co.nz" className={styles['footer__grid-item']}>hello@gatherproductions.co.nz</a>
            <a href="tel:+6421710036" className={styles['footer__grid-item']}>+64 21 710 036</a>
          </div>
          <div className={styles['footer__grid-column']}>
            <a href="https://www.instagram.com/gatherproductions_nz" className={styles['footer__grid-item']} target="_blank" rel="noopener noreferrer">Instagram</a>
            <a href="https://www.linkedin.com/company/gather-productions" className={styles['footer__grid-item']} target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
        </motion.div>
      </div>

      <motion.div 
        ref={videoContainerRef}
        className={styles.footer__logo}
        initial={{ opacity: isAdminMode ? 1 : 0 }}
        animate={{ opacity: videoInView ? 1 : 0 }}
        transition={{ 
          duration: 0.8,
          delay: 0.4, // Appears after grid
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
      >
        <div className={styles.footer__video} ref={videoRef}>
          <svg width="1921" height="1080" viewBox="0 0 1921 1080" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <clipPath id="gp-icon">
                <path d="M1137.33 1044.94H1104.38C1025.26 1031.41 997.885 956.409 987.712 885.712C943.336 963.014 864.302 1016.07 777.738 1035.35C760.431 1039.22 742.639 1040.46 725.552 1044.94H656.926L580.065 1029.84C444.104 988.88 343.755 879.826 315.13 740.283C311.196 721.127 309.86 701.632 305.544 682.623C306.792 657.447 303.842 630.745 305.544 605.761C313.222 492.627 363.88 371.286 433.417 282.094C500.693 195.809 688.164 25.395 800.99 114.499C863.363 163.763 891.283 265.242 817.049 317.09C790.832 335.41 760.636 340.166 731.938 352.761C612.594 405.167 518.059 531.718 539.637 666.769C554.508 759.822 622.929 825.6 721.134 814.077C748.071 810.921 787.397 785.54 772.923 754.625C767.477 742.984 719.123 734.984 702.095 718.426C649.63 667.444 673.411 548.658 745.634 520.18C827.663 487.841 935.953 538.676 989.048 603.016C993.408 511.064 991.338 418.936 994.45 327.043C994.671 320.554 997.166 314.55 997.416 308.062C1001.85 193.005 980.211 13.4606 1138.57 1.79045C1277-8.47047 1493.13 23.2811 1576.01 149.275C1676.52 302.058 1579.68 450.82 1439.33 527.593C1368.01 566.596 1282.12 564.248 1253.38 654.585C1220.4 758.251 1290.2 1025.74 1137.31 1044.94H1137.33ZM1297.89 187.25C1273.34 187.735 1264.35 210.488 1261.15 231.391C1258.3 249.961 1261.1 268.619 1260.97 285.882C1260.71 321.582 1255.88 382.325 1261.01 414.84C1267.27 454.46 1341.3 423.765 1361 408.073C1424.41 357.517 1401.41 238.907 1335.81 200.08C1325.52 193.988 1309.93 187.016 1297.89 187.25Z"/>
                <path d="M1457.84 839.795C1494.87 834.966 1531.41 863.062 1546.06 895.636C1581.79 975.095 1428.17 1086.47 1377.7 989.878C1346.62 930.396 1393.14 848.236 1457.84 839.81V839.795Z"/>
              </clipPath>
            </defs>
          </svg>
          
          {/* Black background shape - fades out when video is actually playing */}
          <motion.div 
            className={styles.footer__blackShape}
            initial={{ opacity: 1 }}
            animate={{ opacity: shouldHideBlackCover ? 0 : 1 }}
            transition={{ 
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          />
          
          {/* Video container - fades in when loaded and in view */}
          <motion.div 
            ref={vimeoContainerRef}
            className={styles.footer__mask}
            style={{ width: '1920px', height: '1080px' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: shouldShowVideo ? 1 : 0 }}
            transition={{ 
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          />
        </div>
      </motion.div>

      <motion.div 
        ref={baseRef}
        className={styles.footer__base}
        initial={{ opacity: isAdminMode ? 1 : 0 }}
        animate={{ opacity: baseInView ? 1 : 0 }}
        transition={{ 
          duration: 0.8,
          delay: 0.6, // Appears after video
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
      >
        <p>&copy; Gather Productions Ltd {new Date().getFullYear()}</p>
        <p><a href="https://www.dieloot.com" target="_blank" rel="noopener noreferrer">Website by Dieloot</a></p>
      </motion.div>
    </footer>
  )
} 